import React, { useState } from 'react';
import { Link } from 'react-router-dom';

function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const faqs = [
    {
      question: "What is Binance Tracker?",
      answer: "Binance Tracker is a real-time cryptocurrency market tracking application that provides comprehensive data about digital assets traded on the Binance exchange. It allows you to monitor prices, volumes, and market trends for hundreds of cryptocurrencies.",
      category: "general"
    },
    {
      question: "How often is the data updated?",
      answer: "The market data is automatically refreshed every 30 seconds on the home page and every 10 seconds on individual coin detail pages. This ensures you always have access to the most current market information.",
      category: "technical"
    },
    {
      question: "Do I need to create an account?",
      answer: "No, you don't need to create an account or provide any personal information. Binance Tracker is completely free to use and doesn't require registration. Simply visit the website and start exploring the markets.",
      category: "general"
    },
    {
      question: "Is my data secure?",
      answer: "Yes, your data is completely secure. We don't store any personal information, trading data, or require authentication. All data is fetched directly from Binance's public API and displayed in your browser. We don't track or store any user information.",
      category: "security"
    },
    {
      question: "Which cryptocurrencies are available?",
      answer: "Binance Tracker displays all cryptocurrencies that are traded against USDT (Tether) on the Binance exchange. We show the top 100 coins by trading volume on the home page, and you can search for any specific coin to view its details.",
      category: "general"
    },
    {
      question: "How do I search for a specific coin?",
      answer: "You can search for any cryptocurrency by typing its symbol (like BTC, ETH, or SOL) or name in the search bar on the home page. The search is case-insensitive and will filter the results in real-time as you type.",
      category: "usage"
    },
    {
      question: "What information is shown for each coin?",
      answer: "For each coin, you can see the current price, 24-hour price change percentage, 24-hour high and low prices, trading volume, and on the detail page, you'll find additional information like bid/ask prices, weighted average price, and hourly price history for the last 24 hours.",
      category: "usage"
    },
    {
      question: "Can I trade cryptocurrencies through this platform?",
      answer: "No, Binance Tracker is a read-only market data platform. It provides information and analytics only. If you want to trade cryptocurrencies, you'll need to use an actual cryptocurrency exchange like Binance, Coinbase, or other trading platforms.",
      category: "general"
    },
    {
      question: "Is the data accurate?",
      answer: "Yes, all data is fetched directly from Binance's official public API, which is the same data source used by the Binance exchange itself. This ensures the highest level of accuracy and reliability.",
      category: "technical"
    },
    {
      question: "Does this platform work on mobile devices?",
      answer: "Yes, Binance Tracker is fully responsive and works seamlessly on desktop computers, tablets, and mobile phones. The interface automatically adapts to your screen size for the best viewing experience.",
      category: "technical"
    },
    {
      question: "Are there any fees to use this platform?",
      answer: "No, Binance Tracker is completely free to use. There are no subscription fees, hidden costs, or premium features. All functionality is available to all users at no charge.",
      category: "general"
    },
    {
      question: "What should I do if I encounter an error?",
      answer: "If you encounter any errors, try refreshing the page. If the problem persists, it might be due to temporary API issues. You can use the retry button that appears when there's an error, or wait a few moments and refresh the page again.",
      category: "technical"
    },
    {
      question: "Can I bookmark specific coins?",
      answer: "Currently, Binance Tracker doesn't have a built-in bookmarking feature. However, you can bookmark specific coin detail pages in your browser for quick access. We're always working on improving the platform based on user feedback.",
      category: "usage"
    },
    {
      question: "How do I interpret the price change percentages?",
      answer: "Green percentages with an upward arrow (↑) indicate positive price changes, while red percentages with a downward arrow (↓) indicate negative price changes. The percentage shows how much the price has changed in the last 24 hours.",
      category: "usage"
    },
    {
      question: "Is there a mobile app available?",
      answer: "Currently, Binance Tracker is a web-based application that works perfectly on mobile browsers. We don't have a dedicated mobile app, but the website is fully optimized for mobile devices and provides the same functionality as the desktop version.",
      category: "technical"
    }
  ];

  const filteredFAQs = searchTerm.trim() === ''
    ? faqs
    : faqs.filter(faq =>
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
      );

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-purple-600 via-pink-600 to-violet-600 bg-clip-text text-transparent">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-600 mb-8">Find answers to common questions about Binance Tracker</p>
          
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-6 py-4 pl-12 pr-12 text-lg border-2 border-purple-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-lg"
            />
            <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 text-center shadow-lg border border-gray-100">
            <div className="text-2xl font-bold text-purple-600">{faqs.length}</div>
            <div className="text-sm text-gray-600">Total FAQs</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-lg border border-gray-100">
            <div className="text-2xl font-bold text-green-600">100%</div>
            <div className="text-sm text-gray-600">Free Access</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-lg border border-gray-100">
            <div className="text-2xl font-bold text-purple-600">24/7</div>
            <div className="text-sm text-gray-600">Support</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-lg border border-gray-100">
            <div className="text-2xl font-bold text-orange-600">0</div>
            <div className="text-sm text-gray-600">Sign-up</div>
          </div>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4 mb-12">
          {filteredFAQs.length > 0 ? (
            filteredFAQs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-xl"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-inset"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <svg
                    className={`w-6 h-6 text-purple-600 flex-shrink-0 transition-transform duration-300 ${
                      openIndex === index ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-5 pt-0">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-xl p-12 text-center shadow-lg border border-gray-200">
              <p className="text-gray-500 text-lg">No FAQs found matching "{searchTerm}"</p>
            </div>
          )}
        </div>

        {/* Helpful Tips Section */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-8 mb-8 border border-purple-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">💡 Helpful Tips</h2>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="text-purple-600 mr-2">•</span>
              <span>Use the search bar on the home page to quickly find specific cryptocurrencies</span>
            </li>
            <li className="flex items-start">
              <span className="text-purple-600 mr-2">•</span>
              <span>Click on any coin card to view detailed market information and price history</span>
            </li>
            <li className="flex items-start">
              <span className="text-purple-600 mr-2">•</span>
              <span>Data refreshes automatically, so you always see the latest market prices</span>
            </li>
            <li className="flex items-start">
              <span className="text-purple-600 mr-2">•</span>
              <span>Green percentages indicate price increases, red indicates decreases</span>
            </li>
          </ul>
        </div>

        {/* Additional Help Section */}
          <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-violet-600 rounded-2xl shadow-xl p-8 text-center text-white mb-8">
          <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-lg mb-6 opacity-90">
            Can't find the answer you're looking for? Feel free to contact us and we'll be happy to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              Contact Us
            </Link>
            <Link
              to="/about"
              className="px-8 py-3 bg-purple-700 text-white rounded-lg font-semibold hover:bg-purple-800 transition-colors shadow-lg"
            >
              Learn More
            </Link>
          </div>
        </div>

        {/* Popular Topics */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Popular Topics</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
              <h3 className="font-semibold text-gray-900 mb-2">Getting Started</h3>
              <p className="text-sm text-gray-600">Learn how to use Binance Tracker effectively</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-100">
              <h3 className="font-semibold text-gray-900 mb-2">Data & Security</h3>
              <p className="text-sm text-gray-600">Understand how we protect your privacy</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
              <h3 className="font-semibold text-gray-900 mb-2">Technical Support</h3>
              <p className="text-sm text-gray-600">Troubleshooting and technical questions</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FAQ;
